import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  useApproveRequestMutation,
  useApprovalSummaryQuery,
  useApprovalsQuery,
  useRejectRequestMutation,
} from "../api/queries";
import { overtimeApi } from "../api/client";
import { OTApprovalFilter, type OTApprovalSearchParams } from "../ui/OTApprovalFilter";
import { OTApprovalListTable } from "../ui/OTApprovalListTable";
import { OTApprovalSummaryCards } from "../ui/OTApprovalSummaryCards";
import { formatDateWithWeekdayLao } from "@/shared/lib/date-time";
import { downloadCsvUtf8, escapeCsvCell } from "@/shared/lib/exportCsvUtf8";
import { Button, Modal, Textarea, toast } from "@devhop/ui";
import type {
  OvertimeRequestListItem,
  OvertimeRequestStatus,
} from "@/modules/hr/overtime/domain/types";
import { Download, HelpCircle, UserPlus } from "lucide-react";
import { OTApprovalListTour, useOTApprovalListTour } from "../tour/OTApprovalListTour";
import { useCallback, useMemo, useState } from "react";

/** Server allows max limit 500 for approvals list. */
const MAX_EXPORT_RECORDS = 500;

const STATUS_LABEL: Record<OvertimeRequestListItem["status"], string> = {
  pending: "ລໍຖ້າອະນຸມັດ",
  approved: "ອະນຸມັດແລ້ວ",
  rejected: "ປະຕິເສດ",
};

function approvalsToCsvRows(rows: OvertimeRequestListItem[]): string[] {
  const header = [
    "ພະນັກງານ",
    "ວັນທີ",
    "ເວລາເລີ່ມ – ສິ້ນສຸດ",
    "ຊົ່ວໂມງ",
    "ເຫດຜົນ",
    "ສະຖານະ",
  ].map(escapeCsvCell).join(",");
  const dataRows = rows.map((row) =>
    [
      row.employeeName,
      formatDateWithWeekdayLao(row.workDate),
      `${row.startTime} – ${row.endTime}`,
      row.hours.toFixed(1),
      row.reason ?? "",
      STATUS_LABEL[row.status],
    ]
      .map(escapeCsvCell)
      .join(","),
  );
  return [header, ...dataRows];
}

export function OTApprovalListPage() {
  const nav = useNavigate({ from: "/app/hr/overtime/approvals" });
  const search = useSearch({ from: "/app/hr/overtime/approvals" }) as OTApprovalSearchParams & {
    offset?: number;
    limit?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  };

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const sortBy = search.sort?.[0]?.field ?? "workDate";
  const sortOrder = (search.sort?.[0]?.dir ?? "desc") as "asc" | "desc";

  const query = useMemo(
    (): {
      offset: number;
      limit: number;
      sort?: Array<{ field: string; dir: "asc" | "desc" }>;
      periodYear: number | undefined;
      periodMonth: number | undefined;
      status: OvertimeRequestStatus | undefined;
      search: string | undefined;
      employeeId: string | undefined;
    } => ({
      offset: search.offset ?? 0,
      limit: search.limit ?? 20,
      sort: search.sort,
      periodYear: search.periodYear ?? currentYear,
      periodMonth: search.periodMonth ?? currentMonth,
      status: (search.status as OvertimeRequestStatus) ?? "pending",
      search: search.search ?? undefined,
      employeeId: search.employeeId ?? undefined,
    }),
    [search, currentYear, currentMonth],
  );

  const listQuery = useApprovalsQuery(query);
  const summaryPeriodYear = search.periodYear ?? currentYear;
  const summaryPeriodMonth = search.periodMonth ?? currentMonth;
  const summaryQuery = useApprovalSummaryQuery(summaryPeriodYear, summaryPeriodMonth);

  const data = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;
  const totalCount = meta?.total ?? 0;
  const limit = meta?.limit ?? 20;
  const offset = search.offset ?? 0;

  const effectiveStatus = (search.status as OvertimeRequestStatus) ?? "pending";
  const isViewingPending = effectiveStatus === "pending";
  const pageTitle =
    effectiveStatus === "approved" && search.employeeId
      ? "ລາຍລະອຽດ OT ອະນຸມັດ (ຈາກສະຫຼຸບ)"
      : effectiveStatus === "approved"
        ? "ລາຍການ OT ອະນຸມັດແລ້ວ"
        : effectiveStatus === "rejected"
          ? "ລາຍການ OT ທີ່ປະຕິເສດ"
          : "ລາຍການອະນຸມັດ OT";
  const pageDescription = isViewingPending
    ? "ເບິ່ງ ແລະ ອະນຸມັດ ຫຼື ປະຕິເສດຄຳຂໍ OT ຈາກທີ່ທ່ານມີ."
    : effectiveStatus === "approved"
      ? "ລາຍການຄຳຂໍ OT ທີ່ອະນຸມັດແລ້ວຕາມເວລາ ແລະ ພະນັກງານ."
      : "ລາຍການຄຳຂໍ OT ທີ່ປະຕິເສດ.";

  const approveMutation = useApproveRequestMutation();
  const rejectMutation = useRejectRequestMutation();

  const [approveModal, setApproveModal] = useState<OvertimeRequestListItem | null>(null);
  const [rejectModal, setRejectModal] = useState<OvertimeRequestListItem | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkApproveIds, setBulkApproveIds] = useState<string[] | null>(null);
  const [bulkRejectIds, setBulkRejectIds] = useState<string[] | null>(null);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);

  const exportQuery = useMemo(
    () => ({
      offset: 0,
      limit: MAX_EXPORT_RECORDS,
      periodYear: search.periodYear ?? currentYear,
      periodMonth: search.periodMonth ?? currentMonth,
      status: (search.status as OvertimeRequestStatus) ?? "pending",
      search: search.search ?? undefined,
      employeeId: search.employeeId ?? undefined,
    }),
    [
      search.periodYear,
      search.periodMonth,
      search.status,
      search.search,
      search.employeeId,
      currentYear,
      currentMonth,
    ],
  );

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const result = await overtimeApi.listApprovals(exportQuery);
      const rows = result?.data ?? [];
      const csv = approvalsToCsvRows(rows).join("\r\n");
      const date = new Date().toISOString().slice(0, 10);
      downloadCsvUtf8(csv, `ot-approvals-${date}.csv`);
      toast.success("ສົ່ງອອກສຳເລັດ");
    } catch {
      // Fetcher already shows error toast; we only reset exporting state
    } finally {
      setExporting(false);
    }
  }, [exportQuery]);

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({
      search: {
        offset: newOffset,
        limit: newLimit,
        sort: search.sort,
        periodYear: search.periodYear ?? currentYear,
        periodMonth: search.periodMonth ?? currentMonth,
        status: search.status ?? "pending",
        search: search.search ?? undefined,
        employeeId: search.employeeId ?? undefined,
      },
    });
  };

  const searchForDetail = useMemo(
    () => ({
      from: "approvals" as const,
      offset: search.offset,
      limit: search.limit,
      sort: search.sort,
      periodYear: search.periodYear,
      periodMonth: search.periodMonth,
      status: search.status,
      search: search.search,
      employeeId: search.employeeId,
    }),
    [
      search.offset,
      search.limit,
      search.sort,
      search.periodYear,
      search.periodMonth,
      search.status,
      search.search,
      search.employeeId,
    ],
  );

  const handleViewDetail = (row: OvertimeRequestListItem) => {
    nav({
      to: "/app/hr/overtime/requests/$id",
      params: { id: row.id },
      search: searchForDetail,
    });
  };
  const handleEdit = (row: OvertimeRequestListItem) => {
    nav({
      to: "/app/hr/overtime/requests/$id",
      params: { id: row.id },
      search: searchForDetail,
    });
  };
  const handleApproveClick = (row: OvertimeRequestListItem) => setApproveModal(row);
  const handleRejectClick = (row: OvertimeRequestListItem) => {
    setRejectReason("");
    setRejectModal(row);
  };

  const handleApproveConfirm = async () => {
    if (!approveModal) return;
    try {
      await approveMutation.mutateAsync(approveModal.id);
      toast.success("ອະນຸມັດຄຳຂໍ OT ແລ້ວ");
      setApproveModal(null);
      listQuery.refetch();
      summaryQuery.refetch();
    } catch {
      toast.error("ອະນຸມັດບໍ່ສຳເລັດ");
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectModal) return;
    try {
      await rejectMutation.mutateAsync({
        id: rejectModal.id,
        rejectionReason: rejectReason.trim() || undefined,
      });
      toast.success("ປະຕິເສດຄຳຂໍ OT ແລ້ວ");
      setRejectModal(null);
      setRejectReason("");
      listQuery.refetch();
      summaryQuery.refetch();
    } catch {
      toast.error("ປະຕິເສດບໍ່ສຳເລັດ");
    }
  };

  const handleBulkApprove = (ids: string[]) => setBulkApproveIds(ids);
  const handleBulkReject = (ids: string[]) => {
    setBulkRejectReason("");
    setBulkRejectIds(ids);
  };

  const handleBulkApproveConfirm = async () => {
    if (!bulkApproveIds?.length) return;
    setBulkLoading(true);
    try {
      const { updated } = await overtimeApi.bulkApprove(bulkApproveIds);
      toast.success(`ອະນຸມັດ ${updated} ຄຳຂໍ OT ແລ້ວ`);
      setBulkApproveIds(null);
      setSelectedIds((prev) => prev.filter((id) => !bulkApproveIds.includes(id)));
      listQuery.refetch();
      summaryQuery.refetch();
    } catch {
      toast.error("ອະນຸມັດບໍ່ສຳເລັດ");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkRejectConfirm = async () => {
    if (!bulkRejectIds?.length) return;
    const reason = bulkRejectReason.trim() || undefined;
    setBulkLoading(true);
    try {
      const { updated } = await overtimeApi.bulkReject(bulkRejectIds, reason);
      toast.success(`ປະຕິເສດ ${updated} ຄຳຂໍ OT ແລ້ວ`);
      setBulkRejectIds(null);
      setBulkRejectReason("");
      setSelectedIds((prev) => prev.filter((id) => !bulkRejectIds.includes(id)));
      listQuery.refetch();
      summaryQuery.refetch();
    } catch {
      toast.error("ປະຕິເສດບໍ່ສຳເລັດ");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: {
          offset: 0,
          limit: search.limit ?? 20,
          sort: [
            { field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" },
          ],
          periodYear: search.periodYear ?? currentYear,
          periodMonth: search.periodMonth ?? currentMonth,
          status: search.status ?? "pending",
          search: search.search ?? undefined,
          employeeId: search.employeeId ?? undefined,
        },
      });
    },
    [
      nav,
      search.limit,
      search.periodYear,
      search.periodMonth,
      search.status,
      search.search,
      search.employeeId,
      currentYear,
      currentMonth,
    ],
  );

  const tour = useOTApprovalListTour();

  return (
    <>
      <Header />
      <Main>
        <OTApprovalListTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4" data-tourid="ot-approval-toolbar">
          <div>
            <div className="mt-2 flex items-center gap-2">
              <h1 className="font-semibold text-2xl">{pageTitle}</h1>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={tour.startTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            </div>
            <p className="text-muted-foreground text-sm">{pageDescription}</p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="default"
              className="gap-2"
              onClick={() =>
                nav({
                  to: "/app/hr/overtime/requests/new-for-employee",
                  search: { employeeId: search.employeeId },
                })
              }
            >
              <UserPlus className="h-4 w-4" />
              ສ້າງຄຳຂໍ OT ໃຫ້ພະນັກງານອື່ນ
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={exporting}
              onClick={handleExport}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              {exporting ? "ກຳລັງສ້າງ..." : "ສົ່ງອອກ"}
            </Button>
          </div>
        </div>

        {isViewingPending && (
          <div className="mb-6" data-tourid="ot-approval-summary">
            <OTApprovalSummaryCards
              data={summaryQuery.data}
              isLoading={summaryQuery.isLoading}
            />
          </div>
        )}

        <div
          className="mb-6 flex flex-col rounded-xl border bg-card pt-2"
          data-tourid="ot-approval-filter"
        >
          <OTApprovalFilter
            searchParams={search}
            fromRoute="/app/hr/overtime/approvals"
          />
          <div data-tourid="ot-approval-table">
            <OTApprovalListTable
            data={data}
            isLoading={listQuery.isLoading}
            offset={offset}
            limit={limit}
            totalCount={totalCount}
            onPaginationChange={handlePaginationChange}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortingChange={handleSortingChange}
            onViewDetail={handleViewDetail}
            onEdit={handleEdit}
            onApprove={handleApproveClick}
            onReject={handleRejectClick}
            selectedIds={selectedIds}
            onSelectedIdsChange={setSelectedIds}
            onBulkApprove={handleBulkApprove}
            onBulkReject={handleBulkReject}
          />
          </div>
        </div>

        <Modal
          open={!!approveModal}
          onOpenChange={(open) => !open && setApproveModal(null)}
          title="ອະນຸມັດຄຳຂໍ OT"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              ອະນຸມັດຄຳຂໍ OT ນີ້ບໍ? ຫຼັງອະນຸມັດຊົ່ວໂມງຈະນຳໄປສະຫຼຸບໃນເງິນເດືອນ.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setApproveModal(null)}>
                ຍົກເລີກ
              </Button>
              <Button onClick={handleApproveConfirm} disabled={approveMutation.isPending}>
                ອະນຸມັດ
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          open={!!rejectModal}
          onOpenChange={(open) => !open && setRejectModal(null)}
          title="ປະຕິເສດຄຳຂໍ OT"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              ປະຕິເສດຄຳຂໍ OT ນີ້ບໍ? ກະລຸນາລະບຸເຫດຜົນ (ບໍ່ບັງຄັບ).
            </p>
            <Textarea
              placeholder="ເຫດຜົນການປະຕິເສດ (ບໍ່ບັງຄັບ)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="resize-none"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejectModal(null)}>
                ຍົກເລີກ
              </Button>
              <Button
                variant="destructive"
                onClick={handleRejectConfirm}
                disabled={rejectMutation.isPending}
              >
                ປະຕິເສດ
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          open={bulkApproveIds != null && bulkApproveIds.length > 0}
          onOpenChange={(open) => !open && setBulkApproveIds(null)}
          title="ອະນຸມັດຄຳຂໍ OT (ທີ່ເລືອກ)"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              ອະນຸມັດ {bulkApproveIds?.length ?? 0} ຄຳຂໍ OT ທີ່ເລືອກບໍ? ຫຼັງອະນຸມັດຊົ່ວໂມງຈະນຳໄປສະຫຼຸບໃນເງິນເດືອນ.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkApproveIds(null)} disabled={bulkLoading}>
                ຍົກເລີກ
              </Button>
              <Button onClick={handleBulkApproveConfirm} disabled={bulkLoading}>
                {bulkLoading ? "ກຳລັງອະນຸມັດ..." : "ອະນຸມັດ"}
              </Button>
            </div>
          </div>
        </Modal>

        <Modal
          open={bulkRejectIds != null && bulkRejectIds.length > 0}
          onOpenChange={(open) => !open && setBulkRejectIds(null)}
          title="ປະຕິເສດຄຳຂໍ OT (ທີ່ເລືອກ)"
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">
              ປະຕິເສດ {bulkRejectIds?.length ?? 0} ຄຳຂໍ OT ທີ່ເລືອກບໍ? ກະລຸນາລະບຸເຫດຜົນ (ບໍ່ບັງຄັບ).
            </p>
            <Textarea
              placeholder="ເຫດຜົນການປະຕິເສດ (ບໍ່ບັງຄັບ)"
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              rows={3}
              className="resize-none"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkRejectIds(null)} disabled={bulkLoading}>
                ຍົກເລີກ
              </Button>
              <Button
                variant="destructive"
                onClick={handleBulkRejectConfirm}
                disabled={bulkLoading}
              >
                {bulkLoading ? "ກຳລັງປະຕິເສດ..." : "ປະຕິເສດ"}
              </Button>
            </div>
          </div>
        </Modal>
      </Main>
    </>
  );
}
